import React, { useState, ChangeEvent, FormEvent } from "react";
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Paper,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { RootState } from "@/store/type";
import { useSelector } from "react-redux";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useHelp from "@/hooks/use-help";
import { LANG } from "@/types";

interface FormData {
  question: string;
  attachment: File | null;
}

const HelpPage: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();
  const [formData, setFormData] = useState<FormData>({
    question: "",
    attachment: null,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { sendQuestion, isLoading, error } = useHelp();
  const authUser = useSelector((state: RootState) => state.auth.authUser);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0] || null;
    setFormData((prevState) => ({
      ...prevState,
      attachment: file,
    }));
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const response = await sendQuestion(formData.question, formData.attachment);
    if (response) {
      setIsSubmitted(true);
      setFormData({ question: "", attachment: null });
    }
  };

  if (isSubmitted) {
    return (
      <Fade in={true} timeout={800}>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.05
              )}, ${alpha(theme.palette.secondary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 80,
                color: theme.palette.success.main,
                mb: 2,
                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.success.main,
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("help.confirmation.title")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 500,
                lineHeight: 1.6,
              }}
            >
              {t("help.confirmation.body")}
            </Typography>
          </Paper>
        </Container>
      </Fade>
    );
  }

  return (
    <Fade in={true} timeout={600}>
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 10,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.8
            )}, ${alpha(theme.palette.background.paper, 0.9)})`,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 700,
              textAlign: "center",
              mb: 3,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {t("help.welcome")} {authUser?.nickName || t("help.host")}! 👋
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 4,
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
            }}
          >
            {t("help.prompt")}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {t("help.errors.sendError")}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.main,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {t("help.writeQuestion")}
            </Typography>

            <TextField
              name="question"
              value={formData.question}
              onChange={handleChange}
              multiline
              rows={5}
              fullWidth
              required
              placeholder={t("help.writeQuestion")}
              inputProps={{
                style: {
                  direction: locale === LANG.AR ? "rtl" : "ltr",
                  textAlign: locale === LANG.AR ? "right" : "left",
                },
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  direction: locale === LANG.AR ? "rtl" : "ltr",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.divider,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              }}
            />

            {formData.attachment && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                <Typography variant="body2">
                  {t("help.attachedFile")}:{" "}
                  <strong>{formData.attachment.name}</strong>
                </Typography>
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<AttachFileIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                {t("help.attachFile")}
                <input
                  type="file"
                  name="attachment"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </Button>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  background: `#18181b`,

                  "&:disabled": {
                    background: theme.palette.grey[300],
                    color: theme.palette.grey[500],
                    boxShadow: "none",
                    transform: "none",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  t("help.sendQuestion")
                )}
              </Button>
            </Box>
          </Box>

          <Typography
            variant="body2"
            sx={{
              mt: 4,
              textAlign: "center",
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
              px: 2,
            }}
          >
            {t("help.note")}
          </Typography>
        </Paper>
      </Container>
    </Fade>
  );
};

export default HelpPage;
